<template>
  <el-card class="box-card">
    <div slot="header" class="clearfix">
      {{ $t("receber-postagens") }}&nbsp;<small>({{ objects.length }} registros)</small>
    </div>

    <!-- Formulário de pesquisa -->
    <el-card shadow="hover">
      <el-form :inline="true" @submit.native.prevent="search">
        <el-form-item :label="$t('cliente')">
          <CustomerList v-model="filter.customer" @selected="onCustomerSelected" />
        </el-form-item>
        <el-form-item :label="$t('motorista')">
          <el-select v-model="filter.driver" filterable clearable v-loading="loadingDrivers"
            :placeholder="$t('selecione-uma-opcao')">
            <el-option v-for="driver of drivers" :key="driver.id" :label="driver.name" :value="driver.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('data-coleta')" prop="name">
          <el-date-picker v-model="filter.range" type="daterange" align="right" unlink-panels clearable
            range-separator="à" format="dd/MM/yyyy" start-placeholder="Inicio" end-placeholder="Fim"></el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="mini" icon="el-icon-search" @click="search">
            {{ $t("pesquisar") }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <template v-if="result.searched">
      <!-- Resumo do recebimento -->
      <el-card shadow="never" class="summary-card">
        <div class="summary-subtitle">
          <template v-if="result.customerName">
            {{ $t("cliente") }}: <strong>{{ result.customerName }}</strong>
            &nbsp;&mdash;&nbsp;
          </template>
          <template v-if="result.driverName">
            {{ $t("motorista") }}: <strong>{{ result.driverName }}</strong>
            &nbsp;&mdash;&nbsp;
          </template>
          {{ $t("periodo") }}: <strong>{{ formatDate(result.range[0]) }} à {{ formatDate(result.range[1]) }}</strong>
        </div>
        <el-row :gutter="10">
          <el-col :xs="24" :sm="8">
            <div class="summary-box summary-collected" @click="activeTab = 'coletados'">
              <p>{{ $t("coletados") }}</p>
              <h2>{{ coletados.length }}</h2>
            </div>
          </el-col>
          <el-col :xs="24" :sm="8">
            <div class="summary-box summary-received" @click="activeTab = 'recebidos'">
              <p>{{ $t("recebidos") }}</p>
              <h2>{{ recebidos.length }}</h2>
            </div>
          </el-col>
          <el-col :xs="24" :sm="8">
            <div class="summary-box" :class="divergentes.length > 0 ? 'summary-divergent' : 'summary-received'"
              @click="activeTab = 'divergentes'">
              <p>{{ $t("divergentes") }}</p>
              <h2>{{ divergentes.length }}</h2>
            </div>
          </el-col>
        </el-row>
        <el-progress class="summary-progress" :percentage="percentualRecebidos" :stroke-width="6"
          :status="percentualRecebidos === 100 ? 'success' : null"></el-progress>
      </el-card>

      <!-- Abas (a mesma tabela é usada para as três, mudando apenas a origem dos dados) -->
      <el-tabs v-model="activeTab">
        <el-tab-pane name="coletados" :label="$t('objetos-coletados') + ' (' + coletados.length + ')'"></el-tab-pane>
        <el-tab-pane name="recebidos" :label="$t('recebidos') + ' (' + recebidos.length + ')'"></el-tab-pane>
        <el-tab-pane name="divergentes" :label="$t('divergentes') + ' (' + divergentes.length + ')'"></el-tab-pane>
      </el-tabs>

      <!-- Filtro rápido client-side da aba atual -->
      <el-input v-model="quickFilter" class="quick-filter" size="mini" clearable prefix-icon="el-icon-search"
        :placeholder="$t('filtrar-etiqueta-remetente')"></el-input>

      <PrepostEdit
        v-if="rowModal.open"
        :tag="rowModal.tag"
        :dialog="rowModal.open"
        :state="'VIEW'"
        tab="edit"
        v-on:close="closeRowModal()"
      />
      <el-table @row-dblclick="openRowModal" :data="currentRows" class="table-collects" size="mini" stripe height="480"
        :row-class-name="rowClassName">
        <el-table-column :label="$t('situacao')" width="100" align="center">
          <template slot-scope="scope">
            <i v-if="scope.row.received" class="el-icon-success icon-received" :title="$t('recebido')"></i>
            <i v-else class="el-icon-warning icon-divergent" :title="$t('coletado-nao-recebido')"></i>
          </template>
        </el-table-column>
        <el-table-column :label="$t('objeto')" prop="tag" width="190" sortable>
          <template slot-scope="scope">
            <TagLink :tag="scope.row.tag" :back="'post'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('motorista')" prop="driverName" width="160" sortable :formatter="formatEmpty">
        </el-table-column>
        <el-table-column :label="$t('remetente')" prop="senderName" sortable :formatter="formatEmpty">
        </el-table-column>
        <el-table-column :label="$t('origem')" width="150">
          <template slot-scope="scope">{{ formatSender(scope.row) }}</template>
        </el-table-column>
        <el-table-column :label="$t('servico')" prop="service" width="120" sortable :formatter="formatEmpty">
        </el-table-column>
        <el-table-column :label="$t('status')" min-width="175">
          <div slot-scope="scope" class="status-cell">
            <el-tag v-if="scope.row.statusDescription" size="mini" :type="statusTagType(scope.row)"
              class="status-tag" :title="scope.row.statusDescription">
              {{ scope.row.statusDescription }}
            </el-tag>
            <span v-else>&mdash;</span>
            <el-tag v-if="scope.row.malha" size="mini" type="info" title="Objeto malha">
              Malha
            </el-tag>
          </div>
        </el-table-column>
        <el-table-column :label="$t('coletado')" prop="collectedAt" width="140" align="center" sortable>
          <template slot-scope="scope">
            <span v-if="scope.row.collectedAt">
              {{ formatFullDateTime(scope.row.collectedAt) }}
            </span>
            <span v-else>&mdash;</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('recebido')" prop="receivedAt" min-width="170" sortable>
          <template slot-scope="scope">
            <span v-if="scope.row.receivedAt">
              {{ formatShortDateTime(scope.row.receivedAt) }}
              <small class="received-by" v-if="scope.row.receivedBy">por {{ scope.row.receivedBy }}</small>
            </span>
            <span v-else>&mdash;</span>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </el-card>
</template>

<script>
import { DriversService } from "@/services/customers/drivers.services";
import { CollectedService } from "@/services/customers/collected.services";
import CustomerList from "@/xpost/views/register/customer/CustomerList";
import TagLink from "@/xpost/views/dashboard/prepost/components/TagLink.vue";
import moment from "moment";
import "moment/locale/pt-br";
import RowModalMixin from "@/xpost/mixins/row-modal";
import PrepostEdit from "@/xpost/views/services/pre.post/PrePostEdit.vue";
import { statusTagTypeByLabel } from "@/xpost/helpers/prepost-status";

export default {
  mixins: [RowModalMixin],
  name: "CollectedMerchandise",
  components: {
    PrepostEdit,
    CustomerList,
    TagLink,
  },
  data() {
    return {
      drivers: [],
      loadingDrivers: false,
      customerName: "",
      filter: {
        customer: "",
        driver: "",
        range: [new Date().setDate(new Date().getDate() - 3), new Date()],
      },
      objects: [],
      result: {
        searched: false,
        customerName: "",
        driverName: "",
        range: [],
      },
      activeTab: "coletados",
      quickFilter: "",
    };
  },
  computed: {
    coletados() {
      return this.objects;
    },
    recebidos() {
      return this.objects.filter((o) => o.received);
    },
    divergentes() {
      return this.objects.filter((o) => !o.received);
    },
    percentualRecebidos() {
      if (this.objects.length === 0) return 0;
      return Math.round((this.recebidos.length / this.objects.length) * 100);
    },
    currentRows() {
      const vm = this;
      let rows = vm.coletados;
      if (vm.activeTab === "recebidos") rows = vm.recebidos;
      if (vm.activeTab === "divergentes") rows = vm.divergentes;

      const term = vm.quickFilter.trim().toLowerCase();
      if (!term) return rows;
      return rows.filter(
        (o) =>
          (o.tag || "").toLowerCase().includes(term) ||
          (o.senderName || "").toLowerCase().includes(term) ||
          (o.driverName || "").toLowerCase().includes(term)
      );
    },
  },
  methods: {
    fetchDrivers() {
      const vm = this;
      vm.loadingDrivers = true;
      DriversService.list()
        .then((response) => {
          vm.drivers = (response || []).sort((a, b) => a.name.localeCompare(b.name));
        })
        .catch((error) => {
          vm.$message({
            type: "error",
            message: vm.errorMessage(error, "Erro ao carregar a lista de motoristas"),
          });
        })
        .finally(() => (vm.loadingDrivers = false));
    },
    onCustomerSelected(id, name) {
      this.customerName = typeof name === "string" ? name : "";
    },
    async search() {
      const vm = this;
      const hasWho = vm.filter.customer || vm.filter.driver;
      if (!hasWho || !vm.filter.range || !vm.filter.range[0] || !vm.filter.range[1]) {
        vm.$message.warning(vm.$t("selecione-cliente-motorista-periodo"));
        return;
      }

      const driver = vm.drivers.find((d) => d.id === vm.filter.driver);
      const loading = vm.$loading(vm.$options.loading);
      try {
        const s = moment(vm.filter.range[0]);
        const f = moment(vm.filter.range[1]);
        const response = await CollectedService.get(
          s.format("YYYY-MM-DD"),
          f.format("YYYY-MM-DD"),
          vm.filter.driver,
          vm.filter.customer
        );
        const records = Array.isArray(response) ? response : [];

        vm.quickFilter = "";
        vm.activeTab = "coletados";

        if (records.length === 0) {
          vm.objects = [];
          vm.result.searched = false;
          vm.$message.warning(vm.$t("motorista-sem-coletas"));
          return;
        }

        vm.objects = records;
        vm.result.searched = true;
        vm.result.customerName = vm.filter.customer ? vm.customerName : "";
        vm.result.driverName = driver ? driver.name : "";
        vm.result.range = vm.filter.range;
      } catch (error) {
        vm.$message({
          type: "error",
          message: vm.errorMessage(error, vm.$t("erro-pesquisar-coletas")),
        });
      } finally {
        loading.close();
      }
    },
    // os services do projeto às vezes rejeitam com string, às vezes com Error/axios
    errorMessage(error, fallback) {
      if (typeof error === "string") return error;
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      if (error && error.message) return error.message;
      return fallback;
    },
    rowClassName({ row }) {
      // na aba "Objetos Coletados", destaca as linhas divergentes
      if (this.activeTab === "coletados" && !row.received) return "row-divergent";
      return "";
    },
    // Mesmo padrão de cores da listagem de Postagem (helper compartilhado)
    statusTagType(row) {
      return statusTagTypeByLabel(row.statusDescription);
    },
    formatEmpty(row, column, cellValue) {
      return cellValue || "—";
    },
    formatSender(row) {
      if (row.senderCity && row.senderState) return `${row.senderCity} - ${row.senderState}`;
      return row.senderCity || row.senderState || "—";
    },
    // aceita Date, timestamp numérico (range do date-picker) e string ISO
    formatDate(value) {
      return moment(value).format("DD/MM/YYYY");
    },
    formatShortDateTime(value) {
      return moment(value).format("DD/MM HH:mm");
    },
    formatFullDateTime(value) {
      return moment(value).format("DD/MM/YYYY HH:mm");
    },
  },
  mounted() {
    const vm = this;
    vm.fetchDrivers();
  },
};
</script>

<style scoped>
.summary-card {
  margin-top: 10px;
  margin-bottom: 10px;
}

.summary-subtitle {
  margin-bottom: 10px;
  color: #606266;
  font-size: 13px;
}

.summary-box {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
}

.summary-box p {
  margin: 0;
  font-size: 12px;
  color: #909399;
  text-transform: uppercase;
}

.summary-box h2 {
  margin: 5px 0 0;
}

.summary-collected h2 {
  color: #409eff;
}

.summary-received h2 {
  color: #67c23a;
}

.summary-divergent {
  border-color: #faecd8;
  background-color: #fdf6ec;
}

.summary-divergent h2 {
  color: #e6a23c;
}

.summary-progress {
  margin-top: 12px;
}

.quick-filter {
  margin-bottom: 10px;
  max-width: 320px;
}

.icon-received {
  color: #67c23a;
  font-size: 16px;
}

.icon-divergent {
  color: #e6a23c;
  font-size: 16px;
}

.received-by {
  color: #909399;
}

.status-cell {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

/* descrições longas (ex.: aferido) não podem empurrar a tag Malha para fora da coluna */
.status-tag {
  max-width: 118px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-collects >>> .row-divergent td {
  background-color: #fdf6ec !important;
}
</style>
